import {
  useDeletePerson,
  useLocations,
  useOptionLookup,
  useOptions,
  useOptionValues,
  usePeople,
  usePerson,
  useRadios,
  useUpsertPerson,
  useUserInfo,
} from '@/hooks';
import { isAuthorised } from '@/utils';
import { useDallasKeys } from '@/utils/config';
import { Box } from '@mui/material';
import { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PersonForm } from './PersonForm';

export function Person() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    query: { data: person },
  } = usePerson(id);
  const {
    query: { data: people },
  } = usePeople();
  const {
    query: { data: radios },
  } = useRadios();
  const { mutate: upsertPerson } = useUpsertPerson();
  const { mutate: deletePerson } = useDeletePerson();
  const {
    query: { data: homeStationNames },
  } = useLocations({ homeStations: true });
  const {
    query: { data: wards },
  } = useLocations({ type: 'Ward' });
  const typeOptions = useOptionValues('groupType');
  const personGroups = useOptionValues('personGroup');
  const {
    query: { data: personRoles },
  } = useOptions('personRole');
  const {
    query: { data: personRanks },
  } = useOptions('personRank');
  const {
    query: { data: activeDirectoryGroups },
  } = useOptions('activeDirectoryGroup');
  const {
    query: { data: personAttributes },
  } = useOptions('personAttribute');
  const personSkills = useOptionValues('personSkill');
  const {
    query: { data: userInfo },
  } = useUserInfo();
  const tripClassifications = useOptionLookup('tripClassification');
  const canEdit = isAuthorised(userInfo?.authorisation, 'people', true);
  const showRfidCardButton = (userInfo?.authorisation?.routes || []).some(
    (route) =>
      `/resources/${useDallasKeys ? 'dallaskeys' : 'rfidcards'}`.startsWith(
        route,
      ),
  );
  const showRadioButton = (userInfo?.authorisation?.routes || []).some(
    (route) => '/resources/radios'.startsWith(route),
  );
  const supervisors = useMemo(
    () =>
      people
        .sort(
          (a, b) =>
            (a.surname ?? '').localeCompare(b.surname ?? '') ||
            (a.forenames ?? '').localeCompare(b.forenames ?? ''),
        )
        .map((person) => ({
          label: `${person.forenames} ${person.surname}`.trim(),
          value: person.code,
        })),
    [people],
  );

  function handleDelete() {
    deletePerson(id, {
      onSuccess: () => {
        navigate('..', { replace: true });
      },
    });
  }

  function handleSubmit(values) {
    upsertPerson(values, {
      onSuccess: (data) => {
        navigate(`../${data.code}`, { replace: true });
      },
    });
  }

  return (
    <Box sx={{ flexGrow: 1, p: 1, bgcolor: 'background.paper' }}>
      <title>
        {[
          'IR3',
          'People',
          person?._id ? `${person.forenames} ${person.surname}` : 'New Person',
        ]
          .filter(Boolean)
          .join(' | ')}
      </title>
      <PersonForm
        key={[person?._id, person?.lastEdit?.time]}
        defaultValues={person}
        canEdit={canEdit}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        supervisors={supervisors}
        homeStationNames={homeStationNames}
        radios={radios}
        wards={wards}
        typeOptions={typeOptions}
        personAttributes={personAttributes}
        personRoles={personRoles}
        personRanks={personRanks}
        personSkills={personSkills}
        personGroups={personGroups}
        activeDirectoryGroups={activeDirectoryGroups}
        tripClassifications={tripClassifications}
        showRfidCardButton={showRfidCardButton}
        showRadioButton={showRadioButton}
      />
    </Box>
  );
}
